import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgDesktop from '../images/background-desktop.jpg';
import bgMobile from '../images/background.png';

const isDesktop = () => window.matchMedia('(min-width: 600px)').matches;

function AuthIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'shield':    return <svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
        case 'mail':      return <svg {...p}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
        case 'arrow-right': return <svg {...p}><path d="M5 12h14M12 5l7 7-7 7"/></svg>;
        case 'arrow-left':  return <svg {...p}><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
        default: return null;
    }
}

export default function ResetPass() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState({});
    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        requestAnimationFrame(() => setReady(true));
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrors({});

        if (!/\S+@\S+\.\S+/.test(email)) {
            setErrors(err => ({ ...err, email: t('errors.email_format') }));
            return;
        }

        setLoading(true);
        const data = new FormData();
        data.append('email', email);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${process.env.REACT_APP_API_URL}/resetpass`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4) {
                setLoading(false);
                if (this.status === 200) {
                    setSent(true);
                } else {
                    let response;
                    try { response = JSON.parse(this.responseText); } catch (_) { response = {}; }
                    if (this.status === 404) {
                        setErrors(err => ({ ...err, general: t('errors.not_found') }));
                    } else if (this.status === 500) {
                        setErrors(err => ({ ...err, general: t('errors.server') }));
                    } else {
                        setErrors(err => ({ ...err, general: response.message || t('errors.unknown') }));
                    }
                }
            }
        };
        xhr.send(data);
    };

    const bg = isDesktop() ? bgDesktop : bgMobile;

    return (
        <div className={`auth-stage${ready ? ' auth-ready' : ''}`}>
            <div className="auth-bg" style={{ backgroundImage: `url(${bg})` }} />
            <div className="auth-vignette" />
            <div className="auth-grain" />

            <div className="auth-card">
                <div className="auth-brand">
                    <AuthIcon name="shield" size={14} />
                    <span className="auth-brand-name">{t('common.wine_police')}</span>
                </div>

                <h1 className="auth-heading">{t('reset_pass.title')}</h1>
                <p className="auth-desc">{t('reset_pass.description')}</p>

                {sent ? (
                    <div className="auth-success-state">
                        <p className="auth-success-text">{t('reset_pass.check_inbox')}</p>
                        <button className="auth-submit auth-submit-mt" onClick={() => navigate('/login')}>
                            {t('common.back')}
                        </button>
                    </div>
                ) : (
                    <form className="auth-form" onSubmit={handleSubmit}>
                        <div className={`auth-field${errors.email ? ' auth-field-err' : ''}`}>
                            <input
                                className="auth-input"
                                type="email"
                                id="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder=" "
                                autoComplete="email"
                            />
                            <label className="auth-label" htmlFor="email"><AuthIcon name="mail" size={11} />{t('common.email')}</label>
                        </div>
                        {errors.email && <p className="auth-error">{errors.email}</p>}

                        {errors.general && (
                            <p className="auth-error auth-error-block">{errors.general}</p>
                        )}

                        <button className="auth-submit" type="submit" disabled={loading}>
                            {loading ? <span className="auth-spinner" /> : <>{t('common.send')}<AuthIcon name="arrow-right" size={15} /></>}
                        </button>
                    </form>
                )}

                <div className="auth-links">
                    <button className="auth-link" onClick={() => navigate('/login')}>
                        <AuthIcon name="arrow-left" size={12} />{t('common.back')}
                    </button>
                </div>
            </div>
        </div>
    );
}
